import { useMemo } from "react";
import { useOrdersRealtime } from "../context/OrdersRealtimeContext";
import { OrderStatus } from "../types/order/IOrder";
import firebase from "firebase/compat/app";

/**
 * Global orders hook backed by the single realtime listener in
 * OrdersRealtimeProvider. Every consumer shares that one subscription, which
 * dramatically reduces Firestore costs by avoiding multiple listeners for the
 * same data.
 *
 * All components should use the filtered hooks below instead of subscribing to
 * orders themselves.
 */
export const useGlobalOrders = () => {
  const { orders, isLoading, isError, error } = useOrdersRealtime();

  return {
    data: orders,
    isLoading,
    isError,
    error,
  };
};

// Stable default references so callers that omit these args don't pass a fresh
// array on every render (which would bust the useMemo below and return a new
// `filteredOrders` identity each render, causing consumer render loops).
const EMPTY_STATUS_FILTER: OrderStatus[] = [];
const EMPTY_ADDITIONAL_WHERE: [
  string,
  firebase.firestore.WhereFilterOp,
  string,
][] = [];

/**
 * Hook for filtered orders with client-side filtering.
 * Use this instead of calling useOrders with specific status filters.
 */
export const useFilteredOrders = (
  statusFilter: OrderStatus[] = EMPTY_STATUS_FILTER,
  additionalWhere: [
    string,
    firebase.firestore.WhereFilterOp,
    string,
  ][] = EMPTY_ADDITIONAL_WHERE
) => {
  const { data: allOrders, isLoading, isError, error } = useGlobalOrders();
  
  const filteredOrders = useMemo(() => {
    if (!allOrders) return [];
    
    let filtered = allOrders;
    
    // Exclude drafts only when they are NOT explicitly requested
    if (!statusFilter.includes(OrderStatus.Draft)) {
      filtered = filtered.filter(order => order.status !== OrderStatus.Draft);
    }

    // Apply status filter
    if (statusFilter.length > 0) {
      filtered = filtered.filter(order => statusFilter.includes(order.status));
    }
    
    // Apply additional where clauses
    additionalWhere.forEach(([field, operator, value]) => {
      filtered = filtered.filter(order => {
        const orderValue = (order as any)[field];
        
        switch (operator) {
          case "==":
            return orderValue === value;
          case "!=":
            return orderValue !== value;
          case "array-contains":
            return Array.isArray(orderValue) && orderValue.includes(value);
          case ">":
            return orderValue > value;
          case ">=":
            return orderValue >= value;
          case "<":
            return orderValue < value;
          case "<=":
            return orderValue <= value;
          case "in":
            return Array.isArray(value) && value.includes(orderValue);
          case "not-in":
            return !Array.isArray(value) || !value.includes(orderValue);
          default:
            return true;
        }
      });
    });
    
    return filtered;
  }, [allOrders, statusFilter, additionalWhere]);
  
  return {
    data: filteredOrders,
    isLoading,
    isError,
    error
  };
};

/**
 * Hook for today's orders with client-side filtering.
 * Replaces the API-based useTodaysOrdersApi for better performance.
 */
export const useTodaysOrders = () => {
  const { data: allOrders, isLoading, isError, error } = useGlobalOrders();
  
  const todaysOrders = useMemo(() => {
    if (!allOrders) return [];
    
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0]; // YYYY-MM-DD format
    
    return allOrders.filter(order => {
      try {
        // Helper function to safely get date string
        const getDateString = (dateValue: any): string => {
          if (!dateValue) return "";
          
          try {
            if (typeof dateValue.toDate === 'function') {
              return dateValue.toDate().toISOString().split('T')[0];
            }
            
            const date = new Date(dateValue);
            if (isNaN(date.getTime())) return "";
            
            return date.toISOString().split('T')[0];
          } catch {
            return "";
          }
        };
        
        // Check createdAt date
        const orderDateStr = getDateString(order.createdAt);
        if (orderDateStr === todayStr) return true;
        
        // Check route date
        if (order.route && order.route.length > 0 && order.route[0].stopDate) {
          const routeDateStr = getDateString(order.route[0].stopDate);
          if (routeDateStr === todayStr) return true;
        }
        
        return false;
      } catch (error) {
        console.warn('Error filtering order by date:', error, order);
        return false;
      }
    });
  }, [allOrders]);
  
  // Calculate status counts
  const statusCounts = useMemo(() => {
    const completed = todaysOrders.filter(order => order.status === OrderStatus.Completed).length;
    const inProgress = todaysOrders.filter(order => order.status === OrderStatus.InProgress).length;
    const pending = todaysOrders.filter(order => 
      [OrderStatus.Draft, OrderStatus.New, OrderStatus.Scheduled].includes(order.status)
    ).length;
    const cancelled = todaysOrders.filter(order => order.status === OrderStatus.Cancelled).length;
    
    return { completed, inProgress, pending, cancelled };
  }, [todaysOrders]);
  
  // Calculate completion percentage
  const completionPercentage = useMemo(() => {
    const total = todaysOrders.length;
    return total > 0 ? Math.round((statusCounts.completed / total) * 100) : 0;
  }, [todaysOrders.length, statusCounts.completed]);
  
  return {
    data: todaysOrders,
    statusCounts,
    completionPercentage,
    isLoading,
    isError,
    error
  };
};

/**
 * Hook for driver-specific orders
 */
export const useDriverOrders = (driverId: string) => {
  return useFilteredOrders([], [["driverId", "==", driverId]]);
};

/**
 * Hook for customer-specific orders
 */
export const useCustomerOrders = (customerId: string) => {
  return useFilteredOrders([], [["customerId", "==", customerId]]);
};

/**
 * Hook for orders by status
 */
export const useOrdersByStatus = (statuses: OrderStatus[]) => {
  return useFilteredOrders(statuses);
}; 
